import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { documents } from '../utils/api';

const DocumentContext = createContext();

const initialState = {
  documents: [],
  tags: [],
  types: [],
  loading: false,
  error: null,
};

const documentReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_DOCUMENTS':
      return { ...state, documents: action.payload };
    case 'ADD_DOCUMENT':
      // Ensure we don't duplicate documents and maintain sort order by created_at DESC
      const updatedDocs = [
        action.payload,
        ...state.documents.filter(doc => doc.id !== action.payload.id)
      ].sort((a, b) => new Date(b.created_at).toISOString() - new Date(a.created_at).toISOString());
      
      return {
        ...state,
        documents: updatedDocs
      };
    case 'UPDATE_DOCUMENT':
      return {
        ...state,
        documents: state.documents.map(doc =>
          doc.id === action.payload.id ? action.payload : doc
        ),
      };
    case 'DELETE_DOCUMENT':
      return {
        ...state,
        documents: state.documents.filter(doc => doc.id !== action.payload),
      };
    case 'SET_TAGS':
      return { ...state, tags: action.payload };
    case 'SET_TYPES':
      return { ...state, types: action.payload };
    default:
      return state;
  }
};

export const DocumentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(documentReducer, initialState);
  const fetchDocuments = useCallback(async (filter) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await documents.getAll(filter);
      dispatch({ type: 'SET_DOCUMENTS', payload: data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const uploadDocument = useCallback(async (formData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { document } = await documents.upload(formData);
      dispatch({ type: 'ADD_DOCUMENT', payload: document });
      return document;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const deleteDocument = useCallback(async (documentId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await documents.delete(documentId);
      dispatch({ type: 'DELETE_DOCUMENT', payload: documentId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const downloadDocument = useCallback(async (documentId) => {
    try {
      const blob = await documents.download(documentId);
      return URL.createObjectURL(blob);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const updateDocument = useCallback(async (documentId, data) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const updatedDoc = await documents.update(documentId, data);
      dispatch({ type: 'UPDATE_DOCUMENT', payload: updatedDoc });
      return updatedDoc;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const value = {
    ...state,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    updateDocument
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};

export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentProvider');
  }
  return context;
};
